/**
 * SSE Client for streaming agent responses
 *
 * Replaces WebSocket with Server-Sent Events for real-time streaming.
 * Uses POST /api/message-stream endpoint which returns an SSE stream.
 */

import { getApiUrl } from './api-url.js';
import { getUIToolDefinitions } from './ui-tools.js';

export type SSEEvent = {
  name: string;
  data: Record<string, unknown>;
  sessionId?: string;
};

export type SSEEventHandler = (event: SSEEvent) => void;
export type SSEErrorHandler = (error: Error) => void;
export type SSECloseHandler = () => void;

/**
 * Parse SSE event from raw text lines
 */
function parseSSEEvent(eventType: string, data: string): SSEEvent | null {
  try {
    const parsed = JSON.parse(data);
    return {
      name: eventType || parsed.name || 'unknown',
      data: parsed,
      sessionId: parsed.sessionId,
    };
  } catch (error) {
    console.error('[SSE] Failed to parse event data:', error, data);
    return null;
  }
}

/**
 * SSE Stream Client
 *
 * Connects to the /api/message-stream endpoint and parses SSE events.
 */
export class SSEStreamClient {
  private abortController: AbortController | null = null;
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || getApiUrl();
  }

  /**
   * Send a message and receive streaming SSE response
   *
   * @param message - The message to send
   * @param sessionId - The session ID
   * @param token - JWT auth token
   * @param onEvent - Handler for each SSE event
   * @param onError - Handler for errors
   * @param onClose - Handler for stream close
   * @param image - Optional image data
   * @param file - Optional file data
   * @param endUserId - Optional end-user ID for multi-tenant isolation
   */
  async streamMessage(
    message: string,
    sessionId: string,
    token: string,
    onEvent: SSEEventHandler,
    onError?: SSEErrorHandler,
    onClose?: SSECloseHandler,
    image?: { base64: string; mimeType: string },
    file?: { base64: string; mimeType: string; filename?: string },
    endUserId?: string
  ): Promise<void> {
    // Cancel any existing stream
    this.cancel();

    this.abortController = new AbortController();

    try {
      const uiTools = getUIToolDefinitions();
      console.log('[SSE] Sending clientTools:', uiTools);

      // Build content - can be string or array of ContentParts for multimodal
      let content: string | Array<{ type: string; [key: string]: unknown }> = message;

      // If we have image or file, convert to multimodal content array
      if (image || file) {
        const parts: Array<{ type: string; [key: string]: unknown }> = [
          { type: 'text', text: message }
        ];

        if (image) {
          parts.push({
            type: 'image',
            image: image.base64,
            mimeType: image.mimeType,
          });
        }

        if (file) {
          parts.push({
            type: 'file',
            data: file.base64,
            mimeType: file.mimeType,
            filename: file.filename,
          });
        }

        content = parts;
      }

      const requestBody: any = {
        content,
        sessionId,
        // Include UI tools so the agent can control the frontend
        clientTools: uiTools,
      };

      console.log('[SSE] Full request body keys:', Object.keys(requestBody));
      console.log('[SSE] clientTools in body:', requestBody.clientTools?.length ?? 'none');

      // Build headers with optional X-End-User-Id for multi-tenant isolation
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      };
      if (endUserId) {
        headers['X-End-User-Id'] = endUserId;
      }

      const response = await fetch(`${this.baseUrl}/message-stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch {
          if (errorText) errorMessage = errorText;
        }
        throw new Error(errorMessage);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      // Parse SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            const data = line.slice(5).trim();
            if (data) {
              const event = parseSSEEvent(currentEventType, data);
              if (event) {
                onEvent(event);
              }
            }
            currentEventType = ''; // Reset after processing data
          } else if (line === '' && currentEventType) {
            // Empty line with event type but no data - reset
            currentEventType = '';
          }
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim()) {
        const lines = buffer.split('\n');
        for (const line of lines) {
          if (line.startsWith('data:')) {
            const data = line.slice(5).trim();
            if (data) {
              const event = parseSSEEvent(currentEventType, data);
              if (event) {
                onEvent(event);
              }
            }
          }
        }
      }

      onClose?.();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Stream was cancelled intentionally
        onClose?.();
        return;
      }
      console.error('[SSE] Stream error:', error);
      onError?.(error instanceof Error ? error : new Error(String(error)));
    } finally {
      this.abortController = null;
    }
  }

  /**
   * Cancel the current stream
   */
  cancel(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Check if a stream is currently active
   */
  isStreaming(): boolean {
    return this.abortController !== null;
  }
}

/**
 * Map SSE event name to intermediate event type
 * Maps agent event names (llm:*, session:*, approval:*) to frontend types
 */
export function mapSSEEventName(sseEventName: string): string {
  const mapping: Record<string, string> = {
    'llm:thinking': 'thinking',
    'llm:chunk': 'chunk',
    'llm:response': 'response',
    'llm:tool-call': 'toolCall',
    'llm:tool-result': 'toolResult',
    'llm:error': 'error',
    'llm:unsupported-input': 'error',
    'session:title-updated': 'sessionTitleUpdated',
    'approval:request': 'approvalRequest',
    'approval:response': 'approvalResolved',
    'ui:action': 'uiAction',
    'trigger:created': 'trigger:created',
    'trigger:processing': 'trigger:processing',
    'trigger:completed': 'trigger:completed',
    'trigger:failed': 'trigger:failed',
    'trigger:pending': 'trigger:pending',
    'trigger:approved': 'trigger:approved',
    'trigger:rejected': 'trigger:rejected',
  };

  return mapping[sseEventName] || sseEventName;
}

/**
 * Transform SSE event data to intermediate event format
 * This ensures compatibility with existing event transformers
 */
export function transformSSEToIntermediateEvent(sseEvent: SSEEvent): Record<string, unknown> | null {
  const data = sseEvent.data;

  switch (sseEvent.name) {
    case 'llm:thinking':
      return {
        type: 'thinking',
        sessionId: data.sessionId,
      };

    case 'llm:chunk':
      return {
        type: 'chunk',
        content: data.content || data.text || '',
        isComplete: data.isComplete,
        sessionId: data.sessionId,
        chunkType: data.type || 'text',
      };

    case 'llm:response':
      return {
        type: 'response',
        text: data.text || '',
        sessionId: data.sessionId,
        reasoning: data.reasoning,
        tokenUsage: data.tokenUsage || data.usage,
        model: data.model,
        provider: data.provider,
        router: data.router,
      };

    case 'llm:tool-call':
      return {
        type: 'toolCall',
        toolName: data.toolName || data.name || '',
        toolArgs: data.args || data.arguments || {},
        toolCallId: data.toolCallId || data.callId || '',
        sessionId: data.sessionId,
      };

    case 'llm:tool-result':
      // Backend sends tool results in 'sanitized' field (SanitizedToolResult)
      // with optional 'rawResult' for debugging. Map to 'result' for frontend.
      console.log('[SSE] Raw tool-result data fields:', {
        hasSanitized: 'sanitized' in data,
        hasRawResult: 'rawResult' in data,
        hasResult: 'result' in data,
        hasToolName: 'toolName' in data,
        toolName: data.toolName,
        sanitizedType: typeof data.sanitized,
        rawResultType: typeof data.rawResult,
        resultType: typeof data.result,
        allKeys: Object.keys(data)
      });
      return {
        type: 'toolResult',
        toolName: data.toolName || data.name || '', // Include toolName for scheduler handling
        toolCallId: data.toolCallId || data.callId || '',
        result: data.sanitized || data.rawResult || data.result,
        success: data.success ?? false, // Default to false if missing - indicates backend issue
        sessionId: data.sessionId,
      };

    case 'llm:error':
    case 'llm:unsupported-input':
      return {
        type: 'error',
        error: data.error || data.message || 'Unknown error',
        sessionId: data.sessionId,
      };

    case 'session:title-updated':
      return {
        type: 'sessionTitleUpdated',
        sessionId: data.sessionId || '',
        title: data.title || '',
      };

    case 'approval:request':
      console.log('[SSE] Received approval:request event:', {
        approvalId: data.approvalId || data.id,
        type: data.type,
        approvalType: data.approvalType,
        sessionId: data.sessionId,
        hasMetadata: !!data.metadata,
      });
      return {
        type: 'approvalRequest',
        approvalId: data.approvalId || data.id || '',
        approvalType: data.approvalType || data.type || 'tool_confirmation',
        metadata: data.metadata || data,
        sessionId: data.sessionId,
      };

    case 'approval:response':
      return {
        type: 'approvalResolved',
        approvalId: data.approvalId || data.id || '',
        status: data.status || 'approved',
        sessionId: data.sessionId,
      };

    case 'ui:action':
      console.log('[SSE] Received ui:action event:', data);
      return {
        type: 'uiAction',
        action: data.action || '',
        args: data.args || {},
        sessionId: data.sessionId,
      };

    case 'error':
      return {
        type: 'error',
        error: data.error || data.message || 'Unknown error',
        sessionId: data.sessionId,
      };

    // Scheduler events
    case 'scheduler:scheduleCreated':
      console.log('[SSE] Received scheduler:scheduleCreated event:', data);
      return {
        type: 'scheduler:scheduleCreated',
        schedule: data.schedule || data,
      };

    case 'scheduler:scheduleUpdated':
      console.log('[SSE] Received scheduler:scheduleUpdated event:', data);
      return {
        type: 'scheduler:scheduleUpdated',
        schedule: data.schedule || data,
      };

    case 'scheduler:scheduleDeleted':
      console.log('[SSE] Received scheduler:scheduleDeleted event:', data);
      return {
        type: 'scheduler:scheduleDeleted',
        scheduleId: data.scheduleId || data.id || '',
        scheduleName: data.scheduleName || data.name || '',
      };

    case 'scheduler:scheduleTriggered':
    case 'scheduler:triggered': {
      console.log('[SSE] Received scheduler:triggered event:', data);
      const innerData = (data.data || {}) as Record<string, unknown>;
      return {
        type: 'scheduler:scheduleTriggered',
        schedule: data.schedule || innerData.schedule || data,
        triggeredAt: data.triggeredAt || innerData.triggeredAt || Date.now(),
        sessionId: data.sessionId || innerData.sessionId,
        executionSessionId: data.sessionId || innerData.sessionId,
      };
    }

    case 'scheduler:executionCompleted':
    case 'scheduler:completed': {
      console.log('[SSE] Received scheduler:completed event:', data);
      const innerData = (data.data || {}) as Record<string, unknown>;
      const log = (data.log || innerData.log || {}) as Record<string, unknown>;
      return {
        type: 'scheduler:executionCompleted',
        schedule: data.schedule || innerData.schedule || data,
        log: log,
        sessionId: data.sessionId || log.sessionId || innerData.sessionId,
      };
    }

    case 'scheduler:executionFailed':
    case 'scheduler:failed': {
      console.log('[SSE] Received scheduler:failed event:', data);
      const innerData = (data.data || {}) as Record<string, unknown>;
      const log = (data.log || innerData.log || {}) as Record<string, unknown>;
      return {
        type: 'scheduler:executionFailed',
        schedule: data.schedule || innerData.schedule || data,
        error: data.error || innerData.error || 'Unknown error',
        log: log,
        sessionId: data.sessionId || log.sessionId || innerData.sessionId,
      };
    }

    // Trigger system events (webhooks, A2A callbacks, async operations)
    case 'trigger:created':
      console.log('[SSE] Received trigger:created event:', data);
      return {
        type: 'trigger:created',
        taskId: data.taskId || '',
        sessionId: data.sessionId || '',
        status: data.status || '',
        source: data.source || {},
      };

    case 'trigger:processing':
      console.log('[SSE] Received trigger:processing event:', data);
      return {
        type: 'trigger:processing',
        taskId: data.taskId || '',
        sessionId: data.sessionId || '',
        status: data.status || '',
      };

    case 'trigger:completed':
      console.log('[SSE] Received trigger:completed event:', data);
      return {
        type: 'trigger:completed',
        taskId: data.taskId || '',
        sessionId: data.sessionId || '',
        status: data.status || '',
        response: data.response,
      };

    case 'trigger:failed':
      console.log('[SSE] Received trigger:failed event:', data);
      return {
        type: 'trigger:failed',
        taskId: data.taskId || '',
        sessionId: data.sessionId || '',
        status: data.status || '',
        error: data.error || 'Unknown error',
      };

    case 'trigger:pending':
      console.log('[SSE] Received trigger:pending event:', data);
      return {
        type: 'trigger:pending',
        taskId: data.taskId || '',
        sessionId: data.sessionId || '',
        status: data.status || '',
        reason: data.reason || '',
      };

    case 'trigger:approved':
      console.log('[SSE] Received trigger:approved event:', data);
      return {
        type: 'trigger:approved',
        taskId: data.taskId || '',
        sessionId: data.sessionId || '',
        status: data.status || '',
      };

    case 'trigger:rejected':
      console.log('[SSE] Received trigger:rejected event:', data);
      return {
        type: 'trigger:rejected',
        taskId: data.taskId || '',
        sessionId: data.sessionId || '',
        status: data.status || '',
        reason: data.reason,
      };

    default:
      // For unknown events, check if it's a scheduler event that wasn't matched
      if (sseEvent.name.startsWith('scheduler:')) {
        console.warn('[SSE] Unhandled scheduler event:', sseEvent.name, data);
      } else {
        console.warn('[SSE] Unknown event type:', sseEvent.name);
      }
      return null;
  }
}
